import Button from "@/lib/Button";
import CheckIcon from "~icons/fa6-solid/check";
import XIcon from "~icons/fa6-solid/x";
import CopyIcon from "~icons/fa6-regular/copy";
import SettingsSectionLayout from "@/lib/SettingsSectionLayout";
import { useParams } from "react-router";
import $api from "@/lib/queries/openapi";
import { useState, useMemo } from "react";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import Select from "react-select";

function formatDateTime(dt: string) {
  return new Date(dt).toLocaleString();
}

export default function QuickBooksDesktop() {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const { data: instanceData, error: instanceError } = $api.useQuery(
    "get",
    "/api/teams/{team_slug}/quickbooks_desktop/instance",
    {
      params: { path: { team_slug: teamSlug! } },
    },
    {
      retry: false,
      refetchInterval: 2000,
    },
  );

  const invalidateInstanceQuery = () => {
    console.warn("Invalidating QuickBooks Desktop instance query");
    queryClient.invalidateQueries({
      queryKey: [
        "get",
        "/api/teams/{team_slug}/quickbooks_desktop/instance",
        { params: { path: { team_slug: teamSlug! } } },
      ],
    });
  };

  const {
    data: incomeAccounts,
    isLoading: loadingAccounts,
    error: accountsError,
  } = $api.useQuery(
    "get",
    "/api/teams/{team_slug}/quickbooks_desktop/accounts",
    {
      params: { path: { team_slug: teamSlug! } },
    },
    {
      retry: false,
      enabled: !!instanceData && instanceData.is_connected,
    },
  );

  const accountOptions = useMemo(() => {
    if (!incomeAccounts) return [];
    return incomeAccounts.map((a) => ({
      label: a.name,
      value: a.id,
    }));
  }, [incomeAccounts]);

  const [password, setPassword] = useState("");

  const { mutate: regeneratePassword, isPending: enabling } = $api.useMutation(
    "post",
    "/api/teams/{team_slug}/quickbooks_desktop/instance/regenerate_password",
    {
      onSuccess: (resp) => {
        setPassword(resp.password);
      },
    },
  );

  const { mutate: updateInstance } = $api.useMutation(
    "patch",
    "/api/teams/{team_slug}/quickbooks_desktop/instance",
    {
      onSuccess: () => {
        invalidateInstanceQuery();
      },
      onError: () => {
        toast.error(`Failed to update`);
      },
    },
  );

  const { mutate: deleteInstance } = $api.useMutation(
    "delete",
    "/api/teams/{team_slug}/quickbooks_desktop/instance",
    {
      onSuccess: () => {
        toast.success("QuickBooks Desktop integration disconnected.");
      },
      onError: () => {
        toast.error("Failed to disconnect QuickBooks Desktop integration.");
      },
    },
  );
  const formattedTimestamp =
    instanceData && instanceData.last_connection_at
      ? formatDateTime(instanceData.last_connection_at)
      : "N/A";
  // Status logic
  let statusContent = null;

  if (instanceError && instanceError?.detail?.startsWith("Not Found")) {
    // Instance not found - means integration is not enabled
    statusContent = (
      <div className="flex gap-4 items-center text-red-600">
        <span>Disabled</span>
      </div>
    );
  } else if (instanceError) {
    // unexpected error - uptool api down or something else
    statusContent = (
      <div className="flex gap-4 items-center text-red-600">
        <XIcon className="size-3" />
        <div>
          Unknown Error. Please try again later or contact Uptool support
        </div>
      </div>
    );
  } else if (instanceData) {
    if (instanceData.is_connected) {
      statusContent = (
        <div className="flex gap-4 items-center">
          <CheckIcon className="text-blue-500" />
          <div>
            Connected. Last successful QuickBooks Web Connector update was at{" "}
            {formattedTimestamp}.
          </div>
        </div>
      );
    } else {
      statusContent = (
        <div>
          <div className="flex gap-4 items-center text-red-600">
            <XIcon className="size-3" />
            <div>Offline</div>
          </div>
          {formattedTimestamp !== "N/A" && (
            <div>
              No connection from QuickBooks Web Connector since{" "}
              {formattedTimestamp}.
            </div>
          )}
          <div>
            Please ensure QuickBooks Web Connector is running and auto-run is
            enabled for the Uptool Application.
          </div>
        </div>
      );
    }
  }

  return (
    <SettingsSectionLayout title="QuickBooks Desktop">
      <div className="flex flex-col gap-8 text-sm items-start">
        {/* QuickBooks Desktop Header */}
        <div className="flex flex-col gap-2">
          <div className="font-bold">QuickBooks Desktop</div>
          <div className="text-gray-500">
            Connect your QuickBooks Desktop instance using QuickBooks Web
            Connector to sync your quotes.
          </div>
        </div>

        {/* Status */}
        <div className="flex flex-col gap-2">
          <div className="font-bold">Status</div>
          {statusContent}
        </div>

        {/* Instructions */}
        <div className="flex flex-col gap-2">
          <div className="font-bold mt-4">Setup Instructions</div>
          <div className="text-gray-500">
            Follow these steps on the computer or server where QuickBooks
            Desktop is running:
          </div>
          <ol className="list-decimal ml-6 text-gray-500 space-y-1">
            <li>
              Make sure QuickBooks is running and the correct Company File is
              open.
            </li>
            <li>
              Install the QuickBooks Web Connector. See Step 1 of the{" "}
              <a
                className="text-blue-600 underline"
                href="https://quickbooks.intuit.com/learn-support/en-us/help-article/install-products/set-quickbooks-web-connector/L4Vp7VI44_US_en_US"
                target="_blank"
                rel="noopener noreferrer"
              >
                Installation Guide.
              </a>
            </li>
            <li>
              Download the QuickBooks Web Connector Configuration (QWC) file
              below.
            </li>
            <li>
              Double click the downloaded QWC file to open it and add Uptool in
              QuickBooks Web Connector.
            </li>
            <li>Follow the prompts to authorize the Uptool connection:</li>
            <ul>
              <li>
                When asked about application permissions, choose "Yes, always
                allow access; even when QuickBooks isn't running."
              </li>
              <li>
                Leave "Allow this application to access personal data..."{" "}
                <b>unchecked</b>.
              </li>
            </ul>
            <li>
              In QuickBooks Web Connector, enter the password you create below
              for the Uptool Application.
              <div className="text-xs">
                Tip: if the password field is resetting before you can save,
                temporarily disable auto-run. Make sure to re-enable when done.
              </div>
            </li>
            <li>
              Make sure to leave the "Every-Min" column empty. If you set a
              value here, you'll need to remove the Uptool Application from
              QuickBooks Web Connector and re-add it.
            </li>
          </ol>
        </div>

        {/* Enable/Download Section */}
        <div className="flex flex-col gap-4 border p-4 rounded w-full max-w-xl bg-gray-50">
          <div className="font-bold mb-2">
            {instanceError
              ? "Enable QuickBooks Desktop Integration"
              : "QuickBooks Desktop Integration"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              className="border self-start"
              disabled={enabling}
              onClick={() => {
                regeneratePassword(
                  {
                    params: { path: { team_slug: teamSlug! } },
                  },
                  {
                    onSuccess: () => {
                      invalidateInstanceQuery();
                    },
                  },
                );
              }}
            >
              {instanceData && instanceData.is_password_set === false
                ? "Generate password"
                : "Regenerate password"}
            </Button>

            {instanceData?.is_password_set !== false && (
              <div className="flex items-center gap-2">
                <span
                  className="px-2 py-1 text-sm w-80 select-all"
                  style={{
                    minWidth: 0,
                    display: "inline-block",
                  }}
                  title={password || "Password"}
                >
                  {password || "•".repeat(32)}
                </span>

                {password && (
                  <Button
                    className="border px-2 py-1"
                    onClick={() => {
                      navigator.clipboard.writeText(password);
                      toast.success("Password copied to clipboard");
                    }}
                    disabled={!password}
                    title="Copy to clipboard"
                  >
                    <CopyIcon className="size-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
          {!instanceError && (
            <>
              <a
                className="flex justify-center items-center whitespace-nowrap rounded-lg disabled:cursor-not-allowed cursor-pointer relative bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2 text-base"
                href={`/api/teams/${encodeURIComponent(teamSlug!)}/quickbooks_desktop/uptool.qwc`}
                download
                tabIndex={0}
              >
                Download QWC File
              </a>
            </>
          )}
        </div>

        {/* Settings Form (only if enabled) */}
        {!instanceError && (
          <>
            <div className="flex flex-col gap-2">
              <div className="font-bold">Settings</div>
              <div className="text-sm">Account for new Items:</div>
              {accountsError ? (
                <div className="text-red-600 text-xs">
                  Failed to load accounts: {accountsError.detail}
                </div>
              ) : (
                <Select
                  placeholder="Select Account"
                  options={accountOptions}
                  isLoading={loadingAccounts}
                  value={
                    accountOptions.find(
                      (opt) => opt.value === instanceData?.income_account_id,
                    ) || null
                  }
                  onChange={(selected) => {
                    updateInstance(
                      {
                        params: { path: { team_slug: teamSlug! } },
                        body: {
                          income_account_id: selected ? selected.value : null,
                        },
                      },
                      {
                        onSuccess: () => {
                          toast.success("Settings updated successfully.");
                        },
                      },
                    );
                  }}
                />
              )}
            </div>
            <div>
              <div className="font-bold">Danger Zone</div>
              <div className="flex justify-end mt-4">
                <Button
                  alert
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to reset QuickBooks Desktop integration? This will remove the connection, settings, and all references to QuickBooks Desktop objects and cannot be undone.",
                      )
                    ) {
                      deleteInstance(
                        {
                          params: { path: { team_slug: teamSlug! } },
                        },
                        {
                          onSuccess: () => {
                            invalidateInstanceQuery();
                          },
                        },
                      );
                    }
                  }}
                >
                  Reset Connection
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </SettingsSectionLayout>
  );
}
